import { useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { thinkingAreas } from "../data/mind";

gsap.registerPlugin(ScrollTrigger);

const THINKING_TOKENS = {
  "01": ["AI-First", "Prompt Craft", "LLM Flows", "Agents", "AI APIs"],
  "02": ["E2E Ownership", "Feature Design", "Edge Cases", "User Outcomes"],
  "03": ["HLD", "Schema-First", "Tradeoffs", "Resilience"],
  "04": ["DX", "UX", "UI Craft", "API Design", "Clarity"],
  "05": ["Impact", "ROI", "Value", "Leverage", "Outcomes"],
};

const ThinkingToken = ({ label, accent }) => (
  <span
    style={{
      fontFamily: "var(--font-robert-medium)",
      fontSize: "clamp(0.52rem,0.67vw,0.61rem)",
      letterSpacing: "0.06em",
      padding: "3px 9px",
      borderRadius: "9999px",
      background: `${accent}12`,
      border: `1px solid ${accent}32`,
      color: accent,
      display: "inline-block",
      userSelect: "none",
      cursor: "default",
    }}
  >
    {label}
  </span>
);

const ThinkingCard = ({ area, isHero = false, style = {} }) => {
  const [hov, setHov] = useState(false);
  const tokens = THINKING_TOKENS[area.index] ?? [];

  return (
    <div
      className="mind-card"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: "16px",
        background: "#FFFFFF",
        border: "1px solid rgba(0,0,0,0.07)",
        boxShadow: hov
          ? "0 8px 28px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.06)"
          : "0 1px 4px rgba(0,0,0,0.05), 0 4px 14px rgba(0,0,0,0.04)",
        transform: hov ? "translateY(-2px)" : "translateY(0)",
        transition:
          "transform 0.28s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.32s ease",
        padding: isHero ? "clamp(20px,2.4vw,30px)" : "clamp(14px,1.7vw,21px)",
        willChange: "transform",
        ...style,
      }}
    >
      {/* Left accent bar */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "18px",
          bottom: "18px",
          left: 0,
          width: "3px",
          borderRadius: "0 3px 3px 0",
          background: area.accent,
        }}
      />

      {/* Index */}
      <span
        style={{
          fontFamily: "var(--font-robert-medium)",
          fontSize: "9px",
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "0.14em",
          color: area.accent,
          marginBottom: isHero ? "8px" : "5px",
          display: "block",
        }}
      >
        {area.index}
      </span>

      {/* Heading */}
      <h3
        style={{
          fontFamily: "var(--font-robert-medium)",
          fontWeight: 700,
          fontSize: isHero
            ? "clamp(1.3rem,2.2vw,2.1rem)"
            : "clamp(0.87rem,1.18vw,1.04rem)",
          lineHeight: 1.08,
          color: "#1C1C1E",
          letterSpacing: "-0.022em",
          marginBottom: isHero ? "10px" : "6px",
        }}
      >
        {area.area}
      </h3>

      {/* Accent rule */}
      <div
        aria-hidden="true"
        style={{
          height: "1px",
          background: `linear-gradient(90deg, ${area.accent}28, rgba(0,0,0,0.04) 55%, transparent)`,
          marginBottom: isHero ? "10px" : "6px",
          flexShrink: 0,
        }}
      />

      {/* Description */}
      <p
        style={{
          fontFamily: "var(--font-robert-regular)",
          fontSize: isHero
            ? "clamp(0.74rem,0.88vw,0.82rem)"
            : "clamp(0.63rem,0.76vw,0.70rem)",
          color: "#48484A",
          lineHeight: 1.72,
          flex: 1,
          paddingBottom: "9px",
        }}
      >
        {area.description}
      </p>

      {/* Thinking tokens */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "4px",
          paddingTop: "8px",
          borderTop: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        {tokens.map((t) => (
          <ThinkingToken key={t} label={t} accent={area.accent} />
        ))}
      </div>
    </div>
  );
};

const Mind = () => {
  const sectionRef = useRef(null);

  useGSAP(
    () => {
      const pref = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;

      if (pref) {
        gsap.set(
          [
            ".mind-act-label",
            ".mind-hdr-title",
            ".mind-avatar-wrap",
            ".mind-card",
            ".mind-hdr",
          ],
          { opacity: 1, y: 0, x: 0, scale: 1, rotateX: 0, skewX: 0 },
        );
        return;
      }

      // Each element starts from a distinct state — layered theatrical reveal
      gsap.set(".mind-act-label", { opacity: 0, y: -20 });
      gsap.set(".mind-hdr-title", { opacity: 0, x: 36, skewX: -4 });
      gsap.set(".mind-avatar-wrap", { opacity: 0, x: -80, scale: 0.9 });
      gsap.set(".mind-card", { opacity: 0, y: 36, scale: 0.88, rotateX: 8 });
      gsap.set(".mind-hdr", { opacity: 0, y: 8 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 68%",
          once: true,
        },
      });

      tl
        // 1. Avatar sweeps in from left — the scene-setter
        .to(".mind-avatar-wrap", {
          opacity: 1,
          x: 0,
          scale: 1,
          duration: 1.15,
          ease: "expo.out",
          transformPerspective: 900,
        })
        // 2. "Act III" label drops in from above
        .to(
          ".mind-act-label",
          { opacity: 1, y: 0, duration: 0.5, ease: "power3.out" },
          "-=0.78",
        )
        // 3. Big headline slides from right with a spring settle
        .to(
          ".mind-hdr-title",
          { opacity: 1, x: 0, skewX: 0, duration: 0.72, ease: "back.out(1.5)" },
          "-=0.34",
        )
        // 4. Cards flip up with 3D spring — the most tactile beat
        .to(
          ".mind-card",
          {
            opacity: 1,
            y: 0,
            scale: 1,
            rotateX: 0,
            stagger: 0.09,
            duration: 0.75,
            ease: "back.out(1.3)",
            transformPerspective: 900,
          },
          "-=0.46",
        )
        // 5. Bottom label quietly fades in last
        .to(
          ".mind-hdr",
          { opacity: 1, y: 0, duration: 0.45, ease: "power2.out" },
          "-=0.32",
        );
    },
    { scope: sectionRef },
  );

  return (
    <section
      id="mind"
      ref={sectionRef}
      className="sticky top-0 z-[20] h-dvh overflow-hidden"
      style={{ background: "#F5F5F7" }}
    >
      {/* Barely-perceptible grain */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.014]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")",
          backgroundRepeat: "repeat",
          backgroundSize: "128px 128px",
        }}
        aria-hidden="true"
      />

      {/* Soft blue blush at bottom-left */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(ellipse 50% 35% at 22% 105%, rgba(10,132,255,0.07) 0%, transparent 65%)",
        }}
      />

      <div className="relative z-10 flex h-full flex-col md:flex-row">
        {/* LEFT: Avatar column */}
        <div className="mind-avatar-wrap w-full md:w-[46%] flex-shrink-0 relative flex items-end justify-center overflow-hidden h-[45vh] md:h-full">
          {/* Ground shadow beneath feet */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              bottom: 0,
              left: "50%",
              transform: "translateX(-50%)",
              width: "50%",
              height: "4%",
              background:
                "radial-gradient(ellipse, rgba(0,0,0,0.10) 0%, transparent 70%)",
              filter: "blur(10px)",
              pointerEvents: "none",
              zIndex: 0,
            }}
          />

          <img
            src="/img/thinking_pose.png"
            alt="Vishvjeet — in thought"
            draggable={false}
            className="h-full w-auto md:h-[101dvh]"
            style={{
              flexShrink: 0,
              display: "block",
              position: "relative",
              zIndex: 1,
              mixBlendMode: "multiply",
              animation: "gentle-float 5.5s ease-in-out infinite",
              filter: "drop-shadow(0 10px 24px rgba(0,0,0,0.13))",
              userSelect: "none",
            }}
          />
        </div>

        {/* RIGHT: Heading + bento + label */}
        <div
          className="flex-1 flex flex-col min-h-0 py-6 pl-5 pr-5 md:py-9 md:pl-6 md:pr-7 lg:py-10 lg:pl-8 lg:pr-12"
          style={{ gap: "clamp(10px,1.5vw,17px)" }}
        >
          {/* Heading — label and title animate independently */}
          <div className="flex-shrink-0">
            <p
              className="mind-act-label font-robert-medium uppercase"
              style={{
                fontSize: "12px",
                letterSpacing: "0.3em",
                color: "#8E8E93",
              }}
            >
              Act III — The Mind
            </p>
            <h2
              className="mind-hdr-title mt-1.5 font-robert-medium font-bold leading-none"
              style={{ fontSize: "clamp(2rem,4.4vw,4rem)", color: "#0A84FF" }}
            >
              What I Think About.
            </h2>
          </div>

          {/* Bento grid */}
          <div
            className="flex-1 min-h-0"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gridTemplateRows: "1.25fr 1fr 1fr",
              gap: "9px",
            }}
          >
            <ThinkingCard
              area={thinkingAreas[0]}
              isHero
              style={{ gridColumn: "1 / -1" }}
            />
            {thinkingAreas.slice(1).map((area) => (
              <ThinkingCard key={area.index} area={area} />
            ))}
          </div>

          {/* Bottom micro-label */}
          <div className="mind-hdr flex-shrink-0">
            <p
              className="font-robert-medium uppercase"
              style={{
                fontSize: "12px",
                letterSpacing: "0.24em",
                color: "rgba(0,0,0,0.5)",
              }}
            >
              Not just a tool list — a way of thinking
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Mind;
